<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <p class="ds-hint">{{ entity }} · Print Holdings</p>
                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" :disabled="isExportExcel" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add category
                    </button>
                </div>
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div v-if="loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="!loading && items.length === 0" class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">📖</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">No print categories found</h2>
                    <p class="text-gray-500 mb-4">Add a print holding category with AA and AD counts.</p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add category
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Key</th>
                                <th class="ds-th">Label</th>
                                <th class="ds-th">AA</th>
                                <th class="ds-th">AD</th>
                                <th class="ds-th">Total</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                <td class="ds-td text-gray-400 font-medium">{{ rowNumber(index) }}</td>
                                <td class="ds-td font-mono text-xs">{{ item.key || "—" }}</td>
                                <td class="ds-td font-medium">{{ item.label || "—" }}</td>
                                <td class="ds-td">{{ formatCount(item.aa_count) }}</td>
                                <td class="ds-td">{{ formatCount(item.ad_count) }}</td>
                                <td class="ds-td font-semibold">{{ rowTotal(item) }}</td>
                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn" @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDelete" type="button" title="Delete" class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ editingId ? "Edit print category" : "Add print category" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">{{ entity }}</p>
                    </div>
                    <div class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="ds-label">Key <span class="text-red-500">*</span></label>
                            <input v-model="form.key" type="text" class="ds-input" placeholder="book" />
                        </div>
                        <div>
                            <label class="ds-label">Label <span class="text-red-500">*</span></label>
                            <input v-model="form.label" type="text" class="ds-input" placeholder="Book" />
                        </div>
                        <div>
                            <label class="ds-label">AA Count</label>
                            <input v-model="form.aa_count" type="number" min="0" class="ds-input" placeholder="0" />
                        </div>
                        <div>
                            <label class="ds-label">AD Count</label>
                            <input v-model="form.ad_count" type="number" min="0" class="ds-input" placeholder="0" />
                        </div>
                    </div>
                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">Cancel</button>
                        <base-button type="button" :disabled="saving" class="bg-teal-700 hover:bg-teal-800 text-white" @click="saveForm">
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import exportManagerMixin from "../../facility/modules/exportManagerMixin.js";

const LIST_QUERY = gql`
    query LibraryPrintCategories($first: Int!, $page: Int!, $entity: String) {
        libraryPrintCategories(first: $first, page: $page, entity: $entity) {
            error
            error_code
            message
            data {
                id
                entity
                key
                label
                aa_count
                ad_count
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateLibraryPrintCategory($input: LibraryPrintCategoryInput!) {
        createLibraryPrintCategory(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateLibraryPrintCategory($id: ID!, $input: LibraryPrintCategoryInput!) {
        updateLibraryPrintCategory(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteLibraryPrintCategory($id: ID!) {
        deleteLibraryPrintCategory(id: $id) {
            error
            error_code
            message
        }
    }
`;

function emptyForm() {
    return { key: "", label: "", aa_count: "0", ad_count: "0" };
}

function formIntegerToValue(value) {
    if (value === null || value === undefined || value === "") return 0;
    var parsed = parseInt(String(value), 10);
    if (isNaN(parsed) || parsed < 0) return 0;
    return parsed;
}

export default {
    name: "PrintHoldingsModule",
    mixins: [exportManagerMixin],
    components: { Pagination, TableShimmer },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, default: "" },
    },
    emits: ["changed"],
    data() {
        return {
            items: [],
            paginatorInfo: null,
            loading: false,
            isExportExcel: false,
            perPage: 25,
            page: 1,
            showFormModal: false,
            editingId: null,
            saving: false,
            form: emptyForm(),
            tableHeaders: ["#", "Key", "Label", "AA", "AD", "Total", "Actions"],
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("library-print-holdings-create");
        },
        canUpdate() {
            return this.hasPermission("library-print-holdings-update");
        },
        canDelete() {
            return this.hasPermission("library-print-holdings-delete");
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1,
                lastItem: pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total),
            };
        },
    },
    created() {
        this.loadList();
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        formatCount(value) {
            if (value === null || value === undefined || value === "") return "—";
            return value;
        },
        rowTotal(item) {
            var aa = item && item.aa_count != null ? Number(item.aa_count) : 0;
            var ad = item && item.ad_count != null ? Number(item.ad_count) : 0;
            if (isNaN(aa)) aa = 0;
            if (isNaN(ad)) ad = 0;
            return aa + ad;
        },
        rowNumber(index) {
            if (!this.paginatorInfo) return index + 1;
            return (this.paginatorInfo.firstItem || 1) + index;
        },
        setPage(page) {
            this.page = page;
            this.loadList();
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadList() {
            var self = this;
            this.loading = true;
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: { first: this.perPage, page: this.page, entity: this.entity },
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload =
                        data && data.libraryPrintCategories ? data.libraryPrintCategories : null;
                    if (payload && !payload.error) {
                        self.items = payload.data || [];
                        self.paginatorInfo = payload.paginatorInfo || null;
                    } else {
                        self.items = [];
                        self.paginatorInfo = null;
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Failed to load print categories",
                        });
                    }
                })
                .catch(function (err) {
                    self.items = [];
                    self.paginatorInfo = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load print categories",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.form = emptyForm();
            this.showFormModal = true;
        },
        openEdit(item) {
            this.editingId = item.id;
            this.form = {
                key: item.key || "",
                label: item.label || "",
                aa_count: item.aa_count === null || item.aa_count === undefined ? "0" : String(item.aa_count),
                ad_count: item.ad_count === null || item.ad_count === undefined ? "0" : String(item.ad_count),
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.editingId = null;
            this.form = emptyForm();
        },
        buildInput() {
            return {
                entity: this.entity,
                key: this.form.key || "",
                label: this.form.label || "",
                aa_count: formIntegerToValue(this.form.aa_count),
                ad_count: formIntegerToValue(this.form.ad_count)
            };
        },
        saveForm() {
            var self = this;
            if (!this.form.key || !String(this.form.key).trim()) {
                this.toastAlert({ icon: "warning", title: "Key is required" });
                return;
            }
            if (!this.form.label || !String(this.form.label).trim()) {
                this.toastAlert({ icon: "warning", title: "Label is required" });
                return;
            }
            this.saving = true;
            var input = this.buildInput();
            var request = this.editingId
                ? this.$apollo.mutate({
                      mutation: UPDATE_MUTATION,
                      variables: { id: this.editingId, input: input },
                  })
                : this.$apollo.mutate({
                      mutation: CREATE_MUTATION,
                      variables: { input: input },
                  });
            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.editingId
                        ? data && data.updateLibraryPrintCategory
                        : data && data.createLibraryPrintCategory;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        confirmDelete(item) {
            var self = this;
            if (!this.$swal) return;
            this.$swal
                .fire({
                    title: "Delete this print category?",
                    html:
                        "<div class='text-left text-sm text-gray-600'><div><strong>Label:</strong> " +
                        (item.label || "—") +
                        "</div></div>",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, delete",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) self.deleteItem(item);
                });
        },
        deleteItem(item) {
            var self = this;
            this.$apollo
                .mutate({ mutation: DELETE_MUTATION, variables: { id: item.id } })
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload =
                        data && data.deleteLibraryPrintCategory
                            ? data.deleteLibraryPrintCategory
                            : null;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Deleted",
                        });
                        if (self.items.length === 1 && self.page > 1) self.page = self.page - 1;
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Delete failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Delete failed",
                    });
                });
        },
        exportFileName() {
            return "ADPOLY_Library_Print_Categories";
        },
        exportSheetName() {
            return "Print Categories";
        },
        _getExportQuery: function () {
            return { query: LIST_QUERY, root: "libraryPrintCategories" };
        },
        _snapshotExportVariables: function () {
            var entity = this.entity;
            return function (page, limit) {
                return { first: limit, page: page, entity: entity };
            };
        },
        mapExportRow(item) {
            var aa = item.aa_count === null || item.aa_count === undefined ? 0 : item.aa_count;
            var ad = item.ad_count === null || item.ad_count === undefined ? 0 : item.ad_count;
            return {
                Key: item.key || "",
                Label: item.label || "",
                AA: aa,
                AD: ad,
                Total: aa + ad,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}
.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}
@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}
.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
}
.ds-hint {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
}
.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}
.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.55rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
}
.ds-btn--primary {
    background: #0f766e;
    color: #ffffff;
    border: 1px solid #0f766e;
}
.ds-btn--ghost {
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
}
.ds-export-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
}
.ds-export-btn:disabled {
    opacity: 0.6;
    pointer-events: none;
}
.ds-export-btn__icon {
    color: #059669;
}
.ds-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
}
.ds-input:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
}
.ds-table-shell {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #ffffff;
}
.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: #0f766e;
}
.ds-th:first-child {
    border-top-left-radius: 0.7rem;
}
.ds-th:last-child {
    border-top-right-radius: 0.7rem;
}
.ds-row:nth-child(even) {
    background: #f0fdfa;
}
.ds-row:hover {
    background: #ccfbf1;
}
.ds-td {
    padding: 0.7rem 1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
}
.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: #f3f4f6;
    margin-left: 0.35rem;
}
.ds-icon-btn--danger:hover {
    background: #fee2e2;
}
.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}
.ds-form__header {
    padding: 1.25rem 1.5rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
}
.ds-form__body {
    padding: 1.25rem 1.5rem;
}
.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
}
.ds-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
